import { useState } from 'react';
import styled from 'styled-components';
import { Flex } from 'reflexbox';
import { NavLink } from 'react-router-dom';

import * as graphql from 'generated/graphql';
import PersonAvatar, {
	PersonAvatarSize,
} from 'packages/kakapo-ui-kit/components/users/PersonAvatar';
import TooltipDark from 'packages/kakapo-ui-kit/components/tooltips/TooltipDark';
import Modal, { ModalBackground, ModalContent } from '../modals/Modal';
import ModalHeader, { ModalHeaderTitle } from '../modals/ModalHeader';

const ExtraUsers = styled(Flex)<{ hovered: boolean; overlap?: boolean }>`
	cursor: pointer;
	width: 38px;
	height: 38px;
	background-color: ${(props) => props.theme.colors.whiteBlue};
	border-radius: 50%;
	transition: border 150ms ease-out;

	&:hover {
		border: 1px solid ${(props) => props.theme.colors.slateBlue};
	}

	${(props) =>
		props.hovered
			? `border: 1px solid ${props.theme.colors.slateBlue};`
			: `border: 1px solid ${props.theme.colors.lightBlueDark};`}

	p {
		font-size: 13px;
		line-height: 16px;
		color: ${(props) => props.theme.colors.slateBlue};
		margin-right: 0;
	}

	${(props) =>
		props.overlap &&
		`
		margin-left: -20px;
	`}
`;

const Element = styled(NavLink)<{ overlap?: boolean }>`
	position: relative;
	margin-right: 6px;
	display: inline-block;

	${(props) =>
		props.overlap &&
		`
		margin-left: -20px;
		:first-child {
			margin-left: 0;
		}

		// Add border to the avatar letter shape. To make the circles distinct
		> div > p {
			border: 1px solid ${props.theme.colors.white};
		}
	`}
`;

const StaticElement = styled.div<{ overlap?: boolean }>`
	position: relative;
	margin-right: 6px;
	display: inline-block;

	${(props) =>
		props.overlap &&
		`
		margin-left: -20px;
		:first-child {
			margin-left: 0;
		}

		// Add border to the avatar letter shape. To make the circles distinct
		> div > p {
			border: 1px solid ${props.theme.colors.white};
		}
	`}
`;

const Tooltip = styled(TooltipDark)`
	top: calc(100% + 8px);
	left: 50%;
	transform: translate(-50%, 0);
	max-width: 184px;

	&:after {
		top: -10px;
		left: 50%;
		transform: translate(-50%, 0);
		border-color: transparent transparent ${(props) => props.theme.colors.antracite} transparent;
	}
`;

const TooltipPersonWrapper = styled(NavLink)`
	display: block;
	margin-bottom: 6px;

	&:first-child {
		margin-top: 4px;
	}
`;

const FullName = styled.p<{ color?: string }>`
	flex: 1;
	min-width: 0;
	color: ${(props) => props.theme.colors[props.color ?? 'white']};
	margin-left: 8px;

	span {
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
		display: block;
	}
`;

const Container = styled(Flex)`
	position: relative;

	p {
		margin-top: 0;
		pointer-events: none;
	}
`;
const TooltipPerson = ({ user }: { user: graphql.User }) => (
	<TooltipPersonWrapper to={`/user/${user.id}`}>
		<Flex alignItems="center">
			<PersonAvatar person={user} size={PersonAvatarSize.Small} />
			<FullName>
				<span>
					{user.firstName} {user.lastName}
				</span>
			</FullName>
		</Flex>
	</TooltipPersonWrapper>
);

function MemberAvatarsListModalItem(props: { user: graphql.User }) {
	return (
		<TooltipPersonWrapper to={`/user/${props.user.id}`}>
			<Flex alignItems="center">
				<PersonAvatar person={props.user} size={PersonAvatarSize.Small} />
				<FullName color="slateBlue">
					<span>
						{props.user.firstName} {props.user.lastName}
					</span>
				</FullName>
			</Flex>
		</TooltipPersonWrapper>
	);
}

export const MemberAvatarsItem = ({ user, overlap }: { user: graphql.User; overlap?: boolean }) => {
	const [isHovered, setIsHovered] = useState(false);

	const toggleHoverState = () => {
		setIsHovered(!isHovered);
	};

	const contents = (
		<>
			<div
				onMouseOver={() => toggleHoverState()}
				onMouseLeave={() => toggleHoverState()}
				onFocus={() => toggleHoverState()}
			>
				<PersonAvatar person={user} size={PersonAvatarSize.Medium} />
			</div>
			<Tooltip isHovered={isHovered} direction="south">
				<span>
					{user.firstName} {user.lastName}
				</span>
			</Tooltip>
		</>
	);

	if (user.id) {
		return (
			<Element to={`/user/${user.id}`} overlap={overlap}>
				{contents}
			</Element>
		);
	} else {
		return <StaticElement overlap={overlap}>{contents}</StaticElement>;
	}
};

function MemberAvatarsListModal(props: {
	title: string;
	users: graphql.User[];
	onDismiss: () => void;
}) {
	return (
		<ModalBackground>
			<Modal>
				<ModalHeader onCancel={() => props.onDismiss()}>
					<ModalHeaderTitle>{props.title}</ModalHeaderTitle>
				</ModalHeader>
				<ModalContent>
					{props.users.map((user) => (
						<MemberAvatarsListModalItem key={user.id} user={user} />
					))}
				</ModalContent>
			</Modal>
		</ModalBackground>
	);
}

export const MemberAvatarsRestList = ({
	allUsers,
	restUsers,
	numberOfExtraUsers,
	overlap,
	title,
}: {
	allUsers: graphql.User[];
	restUsers: graphql.User[];
	numberOfExtraUsers: number;
	overlap?: boolean;
	title: string;
}) => {
	const [isHovered, setIsHovered] = useState(false);

	if (numberOfExtraUsers === 1) {
		return <MemberAvatarsItem user={restUsers[0]} />;
	}

	const toggleHoverState = () => {
		setIsHovered(!isHovered);
	};

	return (
		<Container>
			<ExtraUsers
				overlap={overlap}
				alignItems="center"
				justifyContent="center"
				onClick={() => toggleHoverState()}
				hovered={isHovered}
			>
				<p>+{numberOfExtraUsers}</p>
			</ExtraUsers>
			<Tooltip isHovered={isHovered && restUsers.length <= 5} direction="south">
				<div>
					{restUsers.map((user) => (
						<TooltipPerson key={user.id} user={user} />
					))}
				</div>
			</Tooltip>
			{isHovered && restUsers.length > 5 && (
				<MemberAvatarsListModal
					title={title}
					onDismiss={() => setIsHovered(false)}
					users={allUsers}
				/>
			)}
		</Container>
	);
};
